import * as reduxConstants from "@/state/reduxConstants";
import {IDeliveryProviderPayoutDate} from "@/features/deliveryProvider/api/deliveryProviderModels";
import {Action} from "redux";
import {actionCreator, AppThunkAction} from "@/state/actions";
import {Xtd} from "../../dashboard/api/dashboardModels";
import {
    BusinessUnitAggregation,
    DeliveryProviderAggregation
} from "../../metricDetails/api/metricDetailsModels";
import tasksAPI from "../../tasks/api/tasksAPI";
import applicationAPI from "../api/applicationAPI";
import {
    IDomainSettings,
    IUserSettings,
    IClientConcept,
    IClientConceptPage
} from "../api/applicationModels";
import {ISODateString} from "@/util/dateUtil";
import { find } from "lodash";

interface IChangeAppParameters
    extends Action<typeof reduxConstants.CHANGE_APP_PARAMETERS> {
    clientConcept: IClientConcept;
    clientConceptsForDetect: IClientConcept[];
    clientConceptsForDeliveryPage: IClientConcept[];
    clientConceptsForRecovery: IClientConcept[];
    clientConceptsForDeliveryOperations: IClientConcept[];
    businessUnitId: string;
    fromDate: ISODateString | null;
    date: ISODateString;
    xtd: Xtd;
    businessUnitAggregation: BusinessUnitAggregation;
    deliveryProviderAggregation: DeliveryProviderAggregation;
}

interface IChangeDate extends Action<typeof reduxConstants.CHANGE_DATE> {
    date: ISODateString;
}

interface IChangeFromDate extends Action<typeof reduxConstants.CHANGE_FROM_DATE> {
    fromDate: ISODateString | null;
}

interface IChangeXtd extends Action<typeof reduxConstants.CHANGE_XTD> {
    xtd: Xtd;
}

interface IChangeBusinessUnitAggregation
    extends Action<typeof reduxConstants.CHANGE_BUSINESS_UNIT_AGGREGATION> {
    businessUnitAggregation: BusinessUnitAggregation;
}

interface IChangeDeliveryProviderAggregation
    extends Action<typeof reduxConstants.CHANGE_DELIVERY_PROVIDER_AGGREGATION> {
    deliveryProviderAggregation: DeliveryProviderAggregation;
}

type ISaveUserSettings = Action<typeof reduxConstants.USER_SETTINGS_SAVE>;

interface IConfirmUserSettings
    extends Action<typeof reduxConstants.USER_SETTINGS_CONFIRM> {
    userSettings: IUserSettings;
}

interface IUserSettingsError
    extends Action<typeof reduxConstants.USER_SETTINGS_ERROR> {
    errorMessage: string;
}

interface ISwtichCurrentSelectedBusinessUnit
    extends Action<typeof reduxConstants.SWITCH_CURRENT_SELECTED_BUSINESS_UNIT> {
    businessUnitId: string;
}

interface ISwtichCurrentSelectedDeliveryProvider
    extends Action<typeof reduxConstants.SWITCH_CURRENT_SELECTED_DELIVERY_PROVIDER> {
    deliveryProvider_PK: string;
}

interface IGetOpenTaskCount
    extends Action<typeof reduxConstants.GET_OPEN_TASK_COUNT> {
    openTaskCount: number;
}

interface IReceiveDomainSettings
    extends Action<typeof reduxConstants.RECEIVE_DOMAIN_SETTINGS> {
    domainSettings: IDomainSettings;
}

interface IErrorDomainSettings
    extends Action<typeof reduxConstants.ERROR_DOMAIN_SETTINGS> {
    errorMessage: string;
}

interface IHandleFreemiumMarketingModal
    extends Action<typeof reduxConstants.HANDLE_FREEMIUM_MARKETING_MODAL> {
    freemiumMarketingModalOpen: boolean;
}

interface IHandleMetricConfigurationSaveButton
    extends Action<typeof reduxConstants.HANDLE_METRIC_CONFIGURATION_SAVE_BUTTON> {
    saveButtonClicked: boolean;
}

interface IChangeClientConcept
    extends Action<typeof reduxConstants.CHANGE_CLIENT_CONCEPT> {
    clientConcept: IClientConcept;
    clientConceptsForDetect: IClientConcept[];
    clientConceptsForDeliveryPage: IClientConcept[];
    clientConceptsForRecovery: IClientConcept[];
    clientConceptsForDeliveryOperations: IClientConcept[];
}

interface IHandleScreenResize
    extends Action<typeof reduxConstants.HANDLE_SCREEN_RESIZE> {
    screenWidth: number;
}

interface IGetClientConceptPages
    extends Action<typeof reduxConstants.GET_CLIENT_CONCEPT_PAGES> {
    clientConceptPages: IClientConceptPage[];
}

interface IChangeDeliveryProviderPayoutDatesForBusinessUnit
    extends Action<typeof reduxConstants.CHANGE_DELIVERY_PROVIDER_PAYOUT_DATES> {
    deliveryProviderPayoutDatesForBusinessUnit: IDeliveryProviderPayoutDate[];
}

interface IChangeCurrencyCode
    extends Action<typeof reduxConstants.CHANGE_CURRENCY_CODE> {
    currencyCode: string;
}

const changeAppParameters = actionCreator<IChangeAppParameters>(
    reduxConstants.CHANGE_APP_PARAMETERS
);
const changeFromDate = actionCreator<IChangeFromDate>(
    reduxConstants.CHANGE_FROM_DATE
);
const changeDate = actionCreator<IChangeDate>(reduxConstants.CHANGE_DATE);
const changeXtd = actionCreator<IChangeXtd>(reduxConstants.CHANGE_XTD);
const changeBusinessUnitAggregation = actionCreator<IChangeBusinessUnitAggregation>(
    reduxConstants.CHANGE_BUSINESS_UNIT_AGGREGATION
);
const changeClientConcept = actionCreator<IChangeClientConcept>(
    reduxConstants.CHANGE_CLIENT_CONCEPT
);
const changeDeliveryProviderAggregation =
    actionCreator<IChangeDeliveryProviderAggregation>(
        reduxConstants.CHANGE_DELIVERY_PROVIDER_AGGREGATION
    );
const getClientConceptPages = actionCreator<IGetClientConceptPages>(
    reduxConstants.GET_CLIENT_CONCEPT_PAGES
);

const saveUserSettings = actionCreator<ISaveUserSettings>(
    reduxConstants.USER_SETTINGS_SAVE
);

const confirmUserSettings = actionCreator<IConfirmUserSettings>(
    reduxConstants.USER_SETTINGS_CONFIRM
);
const userSettingsError = actionCreator<IUserSettingsError>(
    reduxConstants.USER_SETTINGS_ERROR
);
const getOpenTaskCount = actionCreator<IGetOpenTaskCount>(
    reduxConstants.GET_OPEN_TASK_COUNT
);

const switchCurrentSelectedBusinessUnit =
    actionCreator<ISwtichCurrentSelectedBusinessUnit>(
        reduxConstants.SWITCH_CURRENT_SELECTED_BUSINESS_UNIT
    );

const switchCurrentSelectedDeliveryProvider =
    actionCreator<ISwtichCurrentSelectedDeliveryProvider>(
        reduxConstants.SWITCH_CURRENT_SELECTED_DELIVERY_PROVIDER
    );

const receiveDomainSettings = actionCreator<IReceiveDomainSettings>(
    reduxConstants.RECEIVE_DOMAIN_SETTINGS
);
const errorDomainSettings = actionCreator<IErrorDomainSettings>(
    reduxConstants.ERROR_DOMAIN_SETTINGS
);

export const handleScreenResize = actionCreator<IHandleScreenResize>(
    reduxConstants.HANDLE_SCREEN_RESIZE
);

export const handleFreemiumMarketingModal =
    actionCreator<IHandleFreemiumMarketingModal>(
        reduxConstants.HANDLE_FREEMIUM_MARKETING_MODAL
    );

export const handleMetricConfigurationSaveButton =
    actionCreator<IHandleMetricConfigurationSaveButton>(
        reduxConstants.HANDLE_METRIC_CONFIGURATION_SAVE_BUTTON
    );

export const changeDeliveryProviderPayoutDatesForBusinessUnit =
    actionCreator<IChangeDeliveryProviderPayoutDatesForBusinessUnit>(
        reduxConstants.CHANGE_DELIVERY_PROVIDER_PAYOUT_DATES
    );

export const changeCurrencyCode = 
    actionCreator<IChangeCurrencyCode>(
        reduxConstants.CHANGE_CURRENCY_CODE
    );

export type ApplicationAction =
    | IChangeAppParameters
    | IChangeFromDate
    | IChangeDate
    | IChangeXtd
    | IChangeClientConcept
    | IGetClientConceptPages
    | ISaveUserSettings
    | IConfirmUserSettings
    | IUserSettingsError
    | ISwtichCurrentSelectedBusinessUnit
    | ISwtichCurrentSelectedDeliveryProvider
    | IGetOpenTaskCount
    | IReceiveDomainSettings
    | IErrorDomainSettings
    | IChangeBusinessUnitAggregation
    | IChangeDeliveryProviderAggregation
    | IHandleFreemiumMarketingModal
    | IHandleScreenResize
    | IHandleMetricConfigurationSaveButton
    | IChangeDeliveryProviderPayoutDatesForBusinessUnit
    | IChangeCurrencyCode;

// ----------------------------------------------------------------------------

export const actionCreators = {
    changeAppParameters(
        clientConcept: IClientConcept,
        businessUnitId: string,
        fromDate: ISODateString | null,
        date: ISODateString,
        xtd: Xtd,
        businessUnitAggregation: BusinessUnitAggregation,
        deliveryProviderAggregation: DeliveryProviderAggregation
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void, getState) => {
            const clientConceptsForDetect =
                getState().application.availableClientConceptsForCoachDetect;
            const clientConceptsForDeliveryPage =
                getState().application.availableClientConceptsForCoachDeliveryPage;
            const clientConceptsForRecovery =
                getState().application.availableClientConceptsForCoachRecovery;
            const clientConceptsForDeliveryOperations =
                getState().application.availableClientConceptsForDeliveryOperations;

            const clientTreeHierarchy = getState().application.clientTreeHierarchy;
            // When switching business units, we will also change the underlying currency code
            const currencyCode = find(clientTreeHierarchy, (bu) => {
                return bu.businessUnitId === businessUnitId;
            })?.currencyCode || "USD"; // default to USD if not found

            dispatch(changeCurrencyCode({currencyCode}));

            dispatch(
                changeAppParameters({
                    clientConcept,
                    clientConceptsForDetect,
                    clientConceptsForDeliveryPage,
                    clientConceptsForRecovery,
                    clientConceptsForDeliveryOperations,
                    businessUnitId,
                    fromDate,
                    date,
                    xtd,
                    businessUnitAggregation,
                    deliveryProviderAggregation
                })
            );
        };
    },

    changeFromDate(
        fromDate: ISODateString | null
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(changeFromDate({fromDate}));
        };
    },

    changeDate(date: ISODateString): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(changeDate({date}));
        };
    },

    changeXtd(xtd: Xtd): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(changeXtd({xtd}));
        };
    },

    changeBusinessUnitAggregation(
        businessUnitAggregation: BusinessUnitAggregation
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(changeBusinessUnitAggregation({businessUnitAggregation}));
        };
    },

    changeDeliveryProviderAggregation(
        deliveryProviderAggregation: DeliveryProviderAggregation
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(
                changeDeliveryProviderAggregation({deliveryProviderAggregation})
            );
        };
    },

    changeClientConcept(
        clientConcept: IClientConcept
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void, getState) => {
            const clientConceptsForDetect =
                getState().application.availableClientConceptsForCoachDetect;
            const clientConceptsForDeliveryPage =
                getState().application.availableClientConceptsForCoachDeliveryPage;
            const clientConceptsForRecovery =
                getState().application.availableClientConceptsForCoachRecovery;
            const clientConceptsForDeliveryOperations =
                getState().application.availableClientConceptsForDeliveryOperations;
            dispatch(
                changeClientConcept({
                    clientConcept,
                    clientConceptsForDetect,
                    clientConceptsForDeliveryPage,
                    clientConceptsForRecovery,
                    clientConceptsForDeliveryOperations
                })
            );
        };
    },

    saveUserSettings(
        userSettings: IUserSettings,
        callback?: () => void
    ): AppThunkAction<ApplicationAction> {
        return (dispatch) => {
            dispatch(saveUserSettings({}));

            applicationAPI
                .saveUserSettings(userSettings)
                .then((data) => {
                    dispatch(confirmUserSettings({userSettings: data}));
                    if (callback) {
                        callback();
                    }
                })
                .catch((err) => {
                    dispatch(userSettingsError({errorMessage: err.message}));
                });
        };
    },

    switchCurrentSelectedBusinessUnit(
        businessUnitId: string
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void, getState) => {
            const clientTreeHierarchy = getState().application.clientTreeHierarchy;
            // When switching business units, we will also change the underlying currency code
            const currencyCode = find(clientTreeHierarchy, (bu) => {
                return bu.businessUnitId === businessUnitId;
            })?.currencyCode || "USD"; // default to USD if not found

            dispatch(changeCurrencyCode({currencyCode}));

            dispatch(switchCurrentSelectedBusinessUnit({businessUnitId}));
        };
    },

    switchCurrentSelectedDeliveryProvider(
        deliveryProvider_PK: string
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(switchCurrentSelectedDeliveryProvider({deliveryProvider_PK}));
        };
    },

    getOpenTaskCount(): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            tasksAPI.fetchOpenTaskCount().then((openTaskCount) => {
                dispatch(getOpenTaskCount({openTaskCount}));
            });
        };
    },

    requestDomainSettings(): AppThunkAction<ApplicationAction> {
        return (dispatch) => {
            applicationAPI
                .getDomainSettings()
                .then((data) => {
                    dispatch(receiveDomainSettings({domainSettings: data}));
                })
                .catch((err) => {
                    dispatch(errorDomainSettings({errorMessage: err.message}));
                });
        };
    },

    handleFreemiumMarketingModal(freemiumMarketingModalOpen: boolean) {
         
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(
                handleFreemiumMarketingModal({
                    freemiumMarketingModalOpen: freemiumMarketingModalOpen
                })
            );
        };
    },

    handleScreenResize(screenWidth: number): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(
                handleScreenResize({
                    screenWidth: screenWidth
                })
            );
        };
    },

    handleMetricConfigurationSaveButton(saveButtonClicked: boolean) {
         
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(
                handleMetricConfigurationSaveButton({
                    saveButtonClicked: saveButtonClicked
                })
            );
        };
    },

    getClientConceptPages(
        callback?: () => void,
        callbackWhenFailed?: () => void
    ): AppThunkAction<ApplicationAction> {
        return (dispatch: (action: ApplicationAction) => void) => {
            applicationAPI
                .getClientConceptPages()
                .then((pages) => {
                    dispatch(getClientConceptPages({clientConceptPages: pages}));
                    if (callback) {
                        callback();
                    }
                })
                .catch(() => {
                    if (callbackWhenFailed) {
                        callbackWhenFailed();
                    }
                });
        };
    },

    changeDeliveryProviderPayoutDatesForBusinessUnit(
        deliveryProviderPayoutDatesForBusinessUnit: IDeliveryProviderPayoutDate[]
    ) {
         
        return (dispatch: (action: ApplicationAction) => void) => {
            dispatch(
                changeDeliveryProviderPayoutDatesForBusinessUnit({
                    deliveryProviderPayoutDatesForBusinessUnit:
                        deliveryProviderPayoutDatesForBusinessUnit
                })
            );
        };
    }
};
